import {
  useEffect,
  useMemo,
  useState,
  type FormEvent,
  type ReactNode,
} from "react";
import Editor from "@monaco-editor/react";
import {
  BrowserRouter,
  Link,
  Navigate,
  Route,
  Routes,
  useNavigate,
  useParams,
} from "react-router-dom";
import {
  ApiError,
  api,
  authStore,
  type BadgeResponse,
  type ClassroomResponse,
  type KidModeCopyResponse,
  type Lesson,
  type LessonSummary,
  type LessonValidateResponse,
  type MeClassroomResponse,
  type ProgressResponse,
  type RunResponse,
  type StreakResponse,
  type TeacherStudentsResponse,
  type UserResponse,
} from "./lib/api";

type LessonsState = {
  loading: boolean;
  data: LessonSummary[];
  error: string | null;
};

type LessonState = {
  loading: boolean;
  data: Lesson | null;
  error: string | null;
};

type AppProgressState = {
  totalXp: number;
  completedLessonIds: string[];
  lessonAttemptMap: Record<string, number>;
  streak: StreakResponse | null;
  badges: BadgeResponse[];
};

type AuthState = {
  bootstrapping: boolean;
  loading: boolean;
  user: UserResponse | null;
  error: string | null;
};

type AuthCardMode = "guest" | "login" | "signup";

type ClassroomState = {
  loading: boolean;
  myClassrooms: MeClassroomResponse[];
  teacherClassrooms: ClassroomResponse[];
  selectedClassroomId: string | null;
  selectedClassroomStudents: TeacherStudentsResponse | null;
  error: string | null;
};

const DEFAULT_PLAYGROUND_CODE = `fn main() {
    println!("Hello, Rust learner!");
}
`;

const DEFAULT_KID_COPY: KidModeCopyResponse = {
  grade_band: "grades_4_6",
  hero_title: "Learn Rust one tiny step at a time",
  hero_subtitle: "Read a small idea, try it, and celebrate every win.",
  lesson_intro_prefix: "Mission",
  hint_button_label: "Need a clue?",
  run_button_label: "Try my code",
  validate_button_label: "Check my mission",
  encouragement_short: [
    "Great effort! Keep going.",
    "Nice try! You are learning fast.",
    "You did it! High five!",
    "One more tiny step!",
  ],
};

const LEVEL_TITLES = [
  "Rust Explorer",
  "Code Sprout",
  "Loop Cadet",
  "Function Maker",
  "Memory Apprentice",
  "Borrow Checker Knight",
  "Lifetime Hero",
];

function classNames(
  ...values: Array<string | false | null | undefined>
): string {
  return values.filter(Boolean).join(" ");
}

function getErrorMessage(error: unknown): string {
  if (error instanceof ApiError) return error.message;
  if (error instanceof Error) return error.message;
  return "Something unexpected happened. Please try again.";
}

function getLevelFromXP(xp: number): number {
  return Math.max(1, Math.floor(xp / 50) + 1);
}

function getLevelTitle(level: number): string {
  return (
    LEVEL_TITLES[Math.min(level - 1, LEVEL_TITLES.length - 1)] ?? "Rust Learner"
  );
}

function progressFromApi(payload: ProgressResponse): AppProgressState {
  const attemptMap: Record<string, number> = {};
  for (const row of payload.lesson_progress) {
    attemptMap[row.lesson_id] = row.attempt_count;
  }

  return {
    totalXp: payload.total_xp,
    completedLessonIds: payload.completed_lesson_ids,
    lessonAttemptMap: attemptMap,
    streak: payload.streak ?? null,
    badges: payload.badges ?? [],
  };
}

function AuthCard({
  mode,
  setMode,
  loading,
  error,
  onGuest,
  onLogin,
  onSignup,
}: {
  mode: AuthCardMode;
  setMode: (mode: AuthCardMode) => void;
  loading: boolean;
  error: string | null;
  onGuest: (nickname: string) => Promise<void>;
  onLogin: (identifier: string, password: string) => Promise<void>;
  onSignup: (
    email: string,
    username: string,
    password: string,
  ) => Promise<void>;
}) {
  const [guestNickname, setGuestNickname] = useState("");
  const [identifier, setIdentifier] = useState("");
  const [loginPassword, setLoginPassword] = useState("");
  const [email, setEmail] = useState("");
  const [signupUsername, setSignupUsername] = useState("");
  const [signupPassword, setSignupPassword] = useState("");

  async function handleGuestSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    await onGuest(guestNickname.trim());
  }

  async function handleLoginSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    await onLogin(identifier.trim(), loginPassword);
  }

  async function handleSignupSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    await onSignup(email.trim(), signupUsername.trim(), signupPassword);
  }

  return (
    <div className="card max-w-xl">
      <h2 className="text-xl font-bold">Start your Rust adventure</h2>
      <p className="mt-1 text-sm text-slate-300">
        You can jump in quickly as a guest, or create an account to keep your
        progress forever.
      </p>

      <div className="mt-4 flex flex-wrap gap-2">
        <button
          className={classNames(
            "btn-secondary",
            mode === "guest" && "border-sky-400",
          )}
          onClick={() => setMode("guest")}
          disabled={loading}
        >
          Guest
        </button>
        <button
          className={classNames(
            "btn-secondary",
            mode === "login" && "border-sky-400",
          )}
          onClick={() => setMode("login")}
          disabled={loading}
        >
          Login
        </button>
        <button
          className={classNames(
            "btn-secondary",
            mode === "signup" && "border-sky-400",
          )}
          onClick={() => setMode("signup")}
          disabled={loading}
        >
          Sign Up
        </button>
      </div>

      {error ? (
        <div className="mt-4 rounded-lg border border-rose-700/50 bg-rose-950/30 p-3 text-sm text-rose-200">
          {error}
        </div>
      ) : null}

      {mode === "guest" ? (
        <form className="mt-4 space-y-3" onSubmit={handleGuestSubmit}>
          <input
            className="input"
            placeholder="Nickname (optional)"
            value={guestNickname}
            onChange={(e) => setGuestNickname(e.target.value)}
            maxLength={32}
          />
          <button
            className="btn-primary w-full"
            type="submit"
            disabled={loading}
          >
            {loading ? "Starting..." : "Start as Guest"}
          </button>
        </form>
      ) : null}

      {mode === "login" ? (
        <form className="mt-4 space-y-3" onSubmit={handleLoginSubmit}>
          <input
            className="input"
            placeholder="Email or username"
            value={identifier}
            onChange={(e) => setIdentifier(e.target.value)}
            required
          />
          <input
            className="input"
            type="password"
            placeholder="Password"
            value={loginPassword}
            onChange={(e) => setLoginPassword(e.target.value)}
            required
          />
          <button
            className="btn-primary w-full"
            type="submit"
            disabled={loading}
          >
            {loading ? "Logging in..." : "Login"}
          </button>
        </form>
      ) : null}

      {mode === "signup" ? (
        <form className="mt-4 space-y-3" onSubmit={handleSignupSubmit}>
          <input
            className="input"
            type="email"
            placeholder="Email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
          />
          <input
            className="input"
            placeholder="Username (optional)"
            value={signupUsername}
            onChange={(e) => setSignupUsername(e.target.value)}
          />
          <input
            className="input"
            type="password"
            placeholder="Password (at least 8 chars with letters and numbers)"
            value={signupPassword}
            onChange={(e) => setSignupPassword(e.target.value)}
            required
          />
          <button
            className="btn-primary w-full"
            type="submit"
            disabled={loading}
          >
            {loading ? "Creating account..." : "Create account"}
          </button>
        </form>
      ) : null}
    </div>
  );
}

function AppLayout({
  children,
  auth,
  progress,
  onLogout,
}: {
  children: ReactNode;
  auth: AuthState;
  progress: AppProgressState;
  onLogout: () => void;
}) {
  const level = getLevelFromXP(progress.totalXp);
  const levelTitle = getLevelTitle(level);
  const earnedBadges = progress.badges.filter((b) => b.awarded).length;

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100">
      <header className="sticky top-0 z-20 border-b border-slate-800 bg-slate-950/95 backdrop-blur">
        <div className="container-page flex items-center justify-between gap-4 py-4">
          <nav className="flex items-center gap-4">
            <Link
              to="/"
              className="text-lg font-bold text-gradient-brand no-underline"
            >
              rustcode.org
            </Link>
            <Link
              to="/learn"
              className="text-sm text-slate-300 hover:text-sky-300 no-underline"
            >
              Learn
            </Link>
            <Link
              to="/classroom"
              className="text-sm text-slate-300 hover:text-sky-300 no-underline"
            >
              Classroom
            </Link>
            <Link
              to="/playground"
              className="text-sm text-slate-300 hover:text-sky-300 no-underline"
            >
              Playground
            </Link>
          </nav>

          <div className="flex items-center gap-2">
            <span className="badge">XP: {progress.totalXp}</span>
            <span className="badge badge-success">
              Lv {level} · {levelTitle}
            </span>
            <span className="badge">
              🔥 {progress.streak?.current_streak ?? 0}
            </span>
            <span className="badge">🏅 {earnedBadges}</span>
            {auth.user ? (
              <>
                <span className="badge">
                  {auth.user.is_guest
                    ? "Guest"
                    : auth.user.username || auth.user.email || "Learner"}
                </span>
                <button className="btn-secondary text-xs" onClick={onLogout}>
                  Logout
                </button>
              </>
            ) : (
              <span className="badge badge-warning">Not signed in</span>
            )}
          </div>
        </div>
      </header>
      <main>{children}</main>
    </div>
  );
}

function StreakCard({ streak }: { streak: StreakResponse | null }) {
  return (
    <div className="card">
      <h3 className="panel-title">Daily Streak</h3>
      <p className="mt-2 text-2xl font-bold">
        🔥 {streak?.current_streak ?? 0} days
      </p>
      <p className="mt-1 text-sm text-slate-300">
        Best: {streak?.longest_streak ?? 0} · Freeze tokens:{" "}
        {streak?.freeze_tokens ?? 0}
      </p>
    </div>
  );
}

function BadgesGrid({ badges }: { badges: BadgeResponse[] }) {
  if (badges.length === 0) {
    return (
      <div className="card">
        <h3 className="panel-title">Achievement Badges</h3>
        <p className="mt-2 text-sm text-slate-400">No badges loaded yet.</p>
      </div>
    );
  }

  return (
    <div className="card">
      <h3 className="panel-title">Achievement Badges</h3>
      <div className="mt-3 grid gap-2 sm:grid-cols-2">
        {badges.map((badge) => (
          <div
            key={badge.key}
            className={classNames(
              "rounded-lg border p-3",
              badge.awarded
                ? "border-emerald-500/40 bg-emerald-950/20"
                : "border-slate-700 bg-slate-900/40 opacity-75",
            )}
          >
            <p className="text-sm font-semibold">
              {badge.icon ? `${badge.icon} ` : ""} {badge.title}
            </p>
            <p className="mt-1 text-xs text-slate-300">{badge.description}</p>
            <p className="mt-2 text-xs text-slate-400">
              Bonus XP: {badge.xp_bonus}{" "}
              {badge.awarded ? "· Earned" : "· Locked"}
            </p>
          </div>
        ))}
      </div>
    </div>
  );
}

function HomePage({
  auth,
  authMode,
  setAuthMode,
  authError,
  onGuestAuth,
  onLoginAuth,
  onSignupAuth,
  progress,
  kidCopy,
}: {
  auth: AuthState;
  authMode: AuthCardMode;
  setAuthMode: (mode: AuthCardMode) => void;
  authError: string | null;
  onGuestAuth: (nickname: string) => Promise<void>;
  onLoginAuth: (identifier: string, password: string) => Promise<void>;
  onSignupAuth: (
    email: string,
    username: string,
    password: string,
  ) => Promise<void>;
  progress: AppProgressState;
  kidCopy: KidModeCopyResponse;
}) {
  return (
    <section className="container-page grid gap-4 lg:grid-cols-2">
      <div className="space-y-4">
        <div className="card p-8">
          <p className="badge badge-success">
            Kid Mode · {kidCopy.grade_band.replace("_", " ")}
          </p>
          <h1 className="mt-4 text-4xl font-extrabold leading-tight">
            {kidCopy.hero_title}
          </h1>
          <p className="mt-3 text-slate-300">{kidCopy.hero_subtitle}</p>
          <ul className="mt-6 space-y-2 text-sm text-slate-200">
            <li>✅ Tiny lessons (2–5 minutes)</li>
            <li>✅ In-browser Rust compiler</li>
            <li>✅ Daily streak & badges</li>
            <li>✅ Classroom-ready teacher mode</li>
          </ul>
          <div className="mt-6 flex gap-3">
            <Link to="/learn" className="btn-primary no-underline">
              Start Learning
            </Link>
            <Link to="/classroom" className="btn-secondary no-underline">
              Classroom
            </Link>
          </div>
        </div>
        <StreakCard streak={progress.streak} />
      </div>

      <div className="space-y-4">
        {auth.user ? (
          <div className="card p-8">
            <h2 className="text-2xl font-bold">
              Welcome back, {auth.user.username || "Rust Learner"}!
            </h2>
            <p className="mt-2 text-slate-300">
              Great to see you again. One tiny mission today keeps your streak
              glowing.
            </p>
            <div className="mt-6 flex gap-3">
              <Link to="/learn" className="btn-primary no-underline">
                Continue Lessons
              </Link>
              <Link to="/playground" className="btn-secondary no-underline">
                Practice Freely
              </Link>
            </div>
          </div>
        ) : (
          <AuthCard
            mode={authMode}
            setMode={setAuthMode}
            loading={auth.loading}
            error={authError}
            onGuest={onGuestAuth}
            onLogin={onLoginAuth}
            onSignup={onSignupAuth}
          />
        )}
        <BadgesGrid badges={progress.badges} />
      </div>
    </section>
  );
}

function LearnPage({
  progress,
  auth,
}: {
  progress: AppProgressState;
  auth: AuthState;
}) {
  const [state, setState] = useState<LessonsState>({
    loading: true,
    data: [],
    error: null,
  });

  useEffect(() => {
    let mounted = true;
    api
      .listLessons()
      .then((res) => {
        if (!mounted) return;
        const sorted = [...res.lessons].sort((a, b) => a.level - b.level);
        setState({ loading: false, data: sorted, error: null });
      })
      .catch((err) => {
        if (!mounted) return;
        setState({ loading: false, data: [], error: getErrorMessage(err) });
      });

    return () => {
      mounted = false;
    };
  }, []);

  const completedSet = useMemo(
    () => new Set(progress.completedLessonIds),
    [progress.completedLessonIds],
  );

  function isUnlocked(index: number): boolean {
    if (index === 0) return true;
    const prev = state.data[index - 1];
    return !!prev && completedSet.has(prev.id);
  }

  return (
    <section className="container-page">
      <div className="mb-4 grid gap-3 lg:grid-cols-3">
        <div className="card lg:col-span-2">
          <h1 className="text-3xl font-bold">Rust Beginner Path</h1>
          <p className="mt-1 text-slate-400">
            Follow in order. Each completed lesson unlocks the next.
          </p>
          <div className="mt-3 flex gap-2">
            <span className="badge">
              Completed: {progress.completedLessonIds.length}
            </span>
            {!auth.user ? (
              <span className="badge badge-warning">
                Sign in to save progress
              </span>
            ) : null}
          </div>
        </div>
        <StreakCard streak={progress.streak} />
      </div>

      {state.loading ? <div className="card">Loading lessons...</div> : null}
      {state.error ? (
        <div className="card border-rose-700/50 text-rose-300">
          Failed to load lessons: {state.error}
        </div>
      ) : null}

      {!state.loading && !state.error ? (
        <div className="grid gap-4">
          {state.data.map((lesson, index) => {
            const unlocked = isUnlocked(index);
            const completed = completedSet.has(lesson.id);

            return (
              <div
                key={lesson.id}
                className={classNames(
                  "card transition",
                  unlocked ? "hover:border-sky-500/50" : "opacity-70",
                  completed ? "border-emerald-500/40" : "",
                )}
              >
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <p className="text-xs font-semibold text-slate-400">
                      Level {lesson.level}
                    </p>
                    <h2 className="text-xl font-bold">{lesson.title}</h2>
                    <p className="text-slate-300">{lesson.subtitle}</p>
                  </div>
                  <div className="flex flex-col items-end gap-2">
                    <span className="badge">+{lesson.xp_reward} XP</span>
                    {completed ? (
                      <span className="badge badge-success">Completed</span>
                    ) : unlocked ? (
                      <span className="badge badge-warning">Available</span>
                    ) : (
                      <span className="badge">Locked</span>
                    )}
                  </div>
                </div>
                <div className="mt-4">
                  {unlocked ? (
                    <Link
                      to={`/learn/foundation_v1/${lesson.id}`}
                      className="btn-primary no-underline"
                    >
                      {completed ? "Review Lesson" : "Start Lesson"}
                    </Link>
                  ) : (
                    <button className="btn-secondary" disabled>
                      Finish previous lesson to unlock
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      ) : null}
    </section>
  );
}

function OutputPanel({
  title,
  value,
  tone = "neutral",
}: {
  title: string;
  value: string;
  tone?: "neutral" | "danger";
}) {
  return (
    <div
      className={classNames(
        "card",
        tone === "danger" && "border-rose-600/50 bg-rose-950/20",
      )}
    >
      <h3 className="panel-title">{title}</h3>
      <pre className="mt-2 max-h-56 overflow-auto whitespace-pre-wrap text-sm text-slate-200 scrollbar-thin">
        {value || "(empty)"}
      </pre>
    </div>
  );
}

function LessonPage({
  auth,
  progress,
  refreshServerProgress,
  kidCopy,
}: {
  auth: AuthState;
  progress: AppProgressState;
  refreshServerProgress: () => Promise<void>;
  kidCopy: KidModeCopyResponse;
}) {
  const navigate = useNavigate();
  const { lessonId = "" } = useParams<{ lessonId: string }>();

  const [lessonsState, setLessonsState] = useState<LessonsState>({
    loading: true,
    data: [],
    error: null,
  });

  const [lessonState, setLessonState] = useState<LessonState>({
    loading: true,
    data: null,
    error: null,
  });

  const [code, setCode] = useState("");
  const [runLoading, setRunLoading] = useState(false);
  const [validateLoading, setValidateLoading] = useState(false);
  const [runResult, setRunResult] = useState<RunResponse | null>(null);
  const [validationResult, setValidationResult] =
    useState<LessonValidateResponse | null>(null);
  const [hintIndex, setHintIndex] = useState(0);

  useEffect(() => {
    let mounted = true;
    setRunResult(null);
    setValidationResult(null);
    setHintIndex(0);

    Promise.all([api.listLessons(), api.getLesson(lessonId)])
      .then(([listRes, lessonRes]) => {
        if (!mounted) return;
        const sorted = [...listRes.lessons].sort((a, b) => a.level - b.level);
        setLessonsState({ loading: false, data: sorted, error: null });
        setLessonState({ loading: false, data: lessonRes.lesson, error: null });
        setCode(
          lessonRes.lesson.challenge.starter_code ||
            lessonRes.lesson.example_code ||
            "",
        );
      })
      .catch((err) => {
        if (!mounted) return;
        const msg = getErrorMessage(err);
        setLessonsState({ loading: false, data: [], error: msg });
        setLessonState({ loading: false, data: null, error: msg });
      });

    return () => {
      mounted = false;
    };
  }, [lessonId]);

  const completedSet = useMemo(
    () => new Set(progress.completedLessonIds),
    [progress.completedLessonIds],
  );
  const sortedLessons = lessonsState.data;
  const currentIndex = sortedLessons.findIndex((item) => item.id === lessonId);
  const previousLesson =
    currentIndex > 0 ? sortedLessons[currentIndex - 1] : null;
  const nextLesson =
    currentIndex >= 0 && currentIndex < sortedLessons.length - 1
      ? sortedLessons[currentIndex + 1]
      : null;
  const locked = !!previousLesson && !completedSet.has(previousLesson.id);

  async function handleRun() {
    if (!code.trim()) return;
    setRunLoading(true);
    setValidationResult(null);
    try {
      const token = authStore.getAccessToken() ?? undefined;
      const result = await api.runCode({ code }, token);
      setRunResult(result);
    } catch (err) {
      setRunResult({
        ok: false,
        stdout: "",
        stderr: getErrorMessage(err),
        compile_error: null,
        duration_ms: 0,
        message: "Run request failed.",
        exit_code: null,
      });
    } finally {
      setRunLoading(false);
    }
  }

  async function handleValidate() {
    if (!code.trim() || !lessonState.data || !auth.user) return;
    setValidateLoading(true);
    try {
      const token = authStore.getAccessToken() ?? undefined;
      const result = await api.validateLesson(
        lessonState.data.id,
        { code },
        token,
      );
      setValidationResult(result);
      setRunResult(result.run);
      await refreshServerProgress();
    } catch (err) {
      setRunResult({
        ok: false,
        stdout: "",
        stderr: getErrorMessage(err),
        compile_error: null,
        duration_ms: 0,
        message: "Validation request failed.",
        exit_code: null,
      });
    } finally {
      setValidateLoading(false);
    }
  }

  if (lessonState.loading || lessonsState.loading) {
    return (
      <section className="container-page">
        <div className="card">Loading lesson...</div>
      </section>
    );
  }

  if (lessonState.error || !lessonState.data) {
    return (
      <section className="container-page">
        <div className="card border-rose-700/50 text-rose-300">
          Failed to load lesson: {lessonState.error ?? "Not found"}
        </div>
      </section>
    );
  }

  if (locked) {
    return (
      <section className="container-page">
        <div className="card border-amber-600/50 bg-amber-950/20">
          <h2 className="text-xl font-bold text-amber-200">
            This lesson is locked
          </h2>
          <p className="mt-2 text-amber-100">
            Complete <strong>{previousLesson?.title}</strong> first.
          </p>
          <div className="mt-4 flex gap-2">
            {previousLesson ? (
              <Link
                className="btn-primary no-underline"
                to={`/learn/foundation_v1/${previousLesson.id}`}
              >
                Go to previous lesson
              </Link>
            ) : null}
            <Link className="btn-secondary no-underline" to="/learn">
              Back to path
            </Link>
          </div>
        </div>
      </section>
    );
  }

  const lesson = lessonState.data;
  const hints = lesson.challenge.hints ?? [];
  const visibleHints = hints.slice(0, hintIndex);
  const completed = completedSet.has(lesson.id);
  const attemptCount = progress.lessonAttemptMap[lesson.id] ?? 0;
  const encouragementList = kidCopy.encouragement_short.length
    ? kidCopy.encouragement_short
    : DEFAULT_KID_COPY.encouragement_short;
  const encouragement =
    encouragementList[Math.min(attemptCount, encouragementList.length - 1)];

  return (
    <section className="container-page">
      <div className="mb-4 flex items-center justify-between gap-2">
        <button onClick={() => navigate("/learn")} className="btn-secondary">
          ← Back to Learn
        </button>
        <div className="flex items-center gap-2">
          <span className="badge">Level {lesson.level}</span>
          <span className="badge">+{lesson.xp_reward} XP</span>
          <span className="badge">Attempts: {attemptCount}</span>
          {completed ? (
            <span className="badge badge-success">Completed</span>
          ) : null}
        </div>
      </div>

      {!auth.user ? (
        <div className="card border-amber-600/50 bg-amber-950/20 mb-4">
          <h3 className="font-semibold text-amber-200">
            Sign in to submit and earn XP
          </h3>
          <p className="mt-1 text-amber-100 text-sm">
            You can still run code, but lesson validation and saved progress
            need a guest/account session.
          </p>
        </div>
      ) : null}

      <div className="grid gap-4 lg:grid-cols-5">
        <div className="space-y-4 lg:col-span-2">
          <div className="card">
            <h1 className="text-2xl font-bold">{lesson.title}</h1>
            <p className="mt-1 text-slate-300">{lesson.subtitle}</p>
            <p className="mt-3 panel-muted">{lesson.explain}</p>
            <p className="mt-3 text-sm text-sky-300">{encouragement}</p>
          </div>

          <div className="card">
            <h2 className="panel-title">Example</h2>
            <pre className="mt-2 overflow-auto rounded-lg bg-slate-950 p-3 text-sm text-slate-200 scrollbar-thin">
              {lesson.example_code}
            </pre>
          </div>

          <div className="card">
            <h2 className="panel-title">
              {kidCopy.lesson_intro_prefix}: {lesson.challenge.prompt}
            </h2>
            <div className="mt-4">
              <h3 className="text-sm font-semibold text-slate-200">Hints</h3>
              <div className="mt-2 space-y-2">
                {visibleHints.length === 0 ? (
                  <p className="text-sm text-slate-400">
                    Try first, then reveal hints only if needed.
                  </p>
                ) : (
                  visibleHints.map((hint, index) => (
                    <div
                      key={index}
                      className="rounded-md border border-slate-700 bg-slate-900 p-2 text-sm"
                    >
                      {hint}
                    </div>
                  ))
                )}
              </div>
              <button
                className="btn-secondary mt-3"
                onClick={() =>
                  setHintIndex((v) => Math.min(v + 1, hints.length))
                }
                disabled={hintIndex >= hints.length}
              >
                {kidCopy.hint_button_label} ({hintIndex}/{hints.length})
              </button>
            </div>
          </div>
        </div>

        <div className="space-y-4 lg:col-span-3">
          <div className="card p-0">
            <div className="flex items-center justify-between border-b border-slate-800 px-4 py-3">
              <h2 className="panel-title">Editor</h2>
              <div className="flex items-center gap-2">
                <button
                  className="btn-secondary"
                  onClick={() => setCode(lesson.challenge.starter_code)}
                >
                  Reset starter
                </button>
                <button
                  className="btn-primary"
                  onClick={handleRun}
                  disabled={runLoading}
                >
                  {runLoading ? "Running..." : kidCopy.run_button_label}
                </button>
                <button
                  className="btn-primary"
                  onClick={handleValidate}
                  disabled={validateLoading || !auth.user}
                >
                  {validateLoading
                    ? "Validating..."
                    : kidCopy.validate_button_label}
                </button>
              </div>
            </div>

            <Editor
              height="380px"
              defaultLanguage="rust"
              theme="vs-dark"
              value={code}
              onChange={(v) => setCode(v ?? "")}
              options={{
                minimap: { enabled: false },
                fontSize: 15,
                lineNumbers: "on",
                automaticLayout: true,
                tabSize: 4,
              }}
            />
          </div>

          {runResult ? (
            <>
              <OutputPanel
                title="Run status"
                value={`${runResult.message} (${runResult.duration_ms} ms)`}
              />
              <OutputPanel title="stdout" value={runResult.stdout} />
              <OutputPanel
                title="stderr / compile errors"
                value={runResult.compile_error || runResult.stderr}
                tone={runResult.ok ? "neutral" : "danger"}
              />
            </>
          ) : null}

          {validationResult ? (
            <div
              className={classNames(
                "card",
                validationResult.passed
                  ? "border-emerald-500/50 bg-emerald-950/20"
                  : "border-amber-500/50 bg-amber-950/20",
              )}
            >
              <h3 className="panel-title">
                {validationResult.passed ? "✅ Great job!" : "❌ Almost there"}
              </h3>
              <p className="mt-2 text-slate-200">{validationResult.message}</p>
              <p className="mt-1 text-sm text-slate-300">
                Score: <strong>{validationResult.score}</strong>
                {validationResult.passed
                  ? ` · XP this run: ${validationResult.xp_awarded}`
                  : ""}
              </p>
              {validationResult.streak ? (
                <p className="mt-1 text-sm text-slate-300">
                  Streak: 🔥 {validationResult.streak.current_streak} days
                </p>
              ) : null}

              <ul className="mt-3 space-y-2 text-sm">
                {validationResult.checks.map((check, idx) => (
                  <li
                    key={`${check.rule_type}-${idx}`}
                    className={classNames(
                      "rounded-md border p-2",
                      check.passed
                        ? "border-emerald-600/40 bg-emerald-900/20 text-emerald-200"
                        : "border-rose-600/40 bg-rose-900/20 text-rose-200",
                    )}
                  >
                    {check.passed ? "✓" : "✗"} {check.description}
                  </li>
                ))}
              </ul>

              {validationResult.badges_earned &&
              validationResult.badges_earned.length > 0 ? (
                <div className="mt-3 rounded-lg border border-sky-600/40 bg-sky-950/20 p-3">
                  <p className="text-sm font-semibold text-sky-200">
                    New badges earned:
                  </p>
                  <ul className="mt-2 space-y-1 text-sm text-sky-100">
                    {validationResult.badges_earned.map((badge) => (
                      <li key={badge.key}>
                        {badge.icon ? `${badge.icon} ` : ""} {badge.title} (+
                        {badge.xp_bonus} XP)
                      </li>
                    ))}
                  </ul>
                </div>
              ) : null}

              {validationResult.passed && nextLesson ? (
                <div className="mt-4">
                  <Link
                    className="btn-primary no-underline"
                    to={`/learn/foundation_v1/${nextLesson.id}`}
                  >
                    Next lesson: {nextLesson.title}
                  </Link>
                </div>
              ) : null}
            </div>
          ) : null}
        </div>
      </div>
    </section>
  );
}

function ClassroomPage({ auth }: { auth: AuthState }) {
  const [state, setState] = useState<ClassroomState>({
    loading: false,
    myClassrooms: [],
    teacherClassrooms: [],
    selectedClassroomId: null,
    selectedClassroomStudents: null,
    error: null,
  });

  const [joinCode, setJoinCode] = useState("");
  const [createName, setCreateName] = useState("Grade 4–6 Rust Squad");
  const [createGradeBand, setCreateGradeBand] = useState("grades_4_6");
  const [busy, setBusy] = useState(false);
  const [infoMessage, setInfoMessage] = useState<string | null>(null);

  const isTeacherLike =
    auth.user?.role === "teacher" || auth.user?.role === "admin";

  async function loadClassroomData() {
    const token = authStore.getAccessToken() ?? undefined;
    if (!token || !auth.user) return;

    setState((prev) => ({ ...prev, loading: true, error: null }));
    try {
      const myRooms = await api.myClassrooms(token);
      let teacherRooms: ClassroomResponse[] = [];
      if (isTeacherLike) {
        teacherRooms = await api.teacherListClassrooms(token);
      }

      setState((prev) => ({
        ...prev,
        loading: false,
        myClassrooms: myRooms,
        teacherClassrooms: teacherRooms,
        selectedClassroomId:
          prev.selectedClassroomId ??
          (teacherRooms.length > 0 ? teacherRooms[0].id : null),
        selectedClassroomStudents: prev.selectedClassroomStudents,
        error: null,
      }));
    } catch (err) {
      setState((prev) => ({
        ...prev,
        loading: false,
        error: getErrorMessage(err),
      }));
    }
  }

  useEffect(() => {
    void loadClassroomData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auth.user?.id, isTeacherLike]);

  async function handleJoinClassroom(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!joinCode.trim()) return;
    const token = authStore.getAccessToken() ?? undefined;
    if (!token) return;

    setBusy(true);
    setInfoMessage(null);
    try {
      const joined = await api.joinClassroom(
        { invite_code: joinCode.trim() },
        token,
      );
      setJoinCode("");
      setInfoMessage(`Joined classroom: ${joined.name}`);
      await loadClassroomData();
    } catch (err) {
      setInfoMessage(getErrorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  async function handleCreateClassroom(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const token = authStore.getAccessToken() ?? undefined;
    if (!token || !createName.trim()) return;

    setBusy(true);
    setInfoMessage(null);
    try {
      const created = await api.teacherCreateClassroom(
        { name: createName.trim(), grade_band: createGradeBand },
        token,
      );
      setInfoMessage(
        `Created classroom "${created.name}" with invite code ${created.invite_code}`,
      );
      await loadClassroomData();
    } catch (err) {
      setInfoMessage(getErrorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  async function handleLoadStudents(classroomId: string) {
    const token = authStore.getAccessToken() ?? undefined;
    if (!token) return;
    setState((prev) => ({
      ...prev,
      loading: true,
      selectedClassroomId: classroomId,
      error: null,
    }));
    try {
      const roster = await api.teacherClassroomStudents(classroomId, token);
      setState((prev) => ({
        ...prev,
        loading: false,
        selectedClassroomStudents: roster,
      }));
    } catch (err) {
      setState((prev) => ({
        ...prev,
        loading: false,
        error: getErrorMessage(err),
      }));
    }
  }

  async function handleRotateInvite(classroomId: string) {
    const token = authStore.getAccessToken() ?? undefined;
    if (!token) return;
    setBusy(true);
    setInfoMessage(null);
    try {
      const updated = await api.teacherRotateInviteCode(classroomId, token);
      setInfoMessage(`Invite code rotated. New code: ${updated.invite_code}`);
      await loadClassroomData();
      await handleLoadStudents(classroomId);
    } catch (err) {
      setInfoMessage(getErrorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  if (!auth.user) {
    return (
      <section className="container-page">
        <div className="card border-amber-600/50 bg-amber-950/20">
          <h2 className="text-xl font-bold text-amber-200">Sign in first</h2>
          <p className="mt-2 text-amber-100">
            Classroom features need a guest or full account session.
          </p>
        </div>
      </section>
    );
  }

  return (
    <section className="container-page space-y-4">
      <div className="card">
        <h1 className="text-3xl font-bold">Classroom</h1>
        <p className="mt-1 text-slate-300">
          Join a class with an invite code, or create one if you are a teacher.
        </p>
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <form className="card space-y-3" onSubmit={handleJoinClassroom}>
          <h2 className="panel-title">Join with invite code</h2>
          <input
            className="input"
            placeholder="Example: AB12CD34"
            value={joinCode}
            onChange={(e) => setJoinCode(e.target.value.toUpperCase())}
            maxLength={16}
          />
          <button className="btn-primary" type="submit" disabled={busy}>
            {busy ? "Joining..." : "Join Classroom"}
          </button>
        </form>

        <div className="card">
          <h2 className="panel-title">My classrooms</h2>
          {state.loading ? <p className="mt-2 text-sm">Loading...</p> : null}
          {state.myClassrooms.length === 0 ? (
            <p className="mt-2 text-sm text-slate-400">
              You are not in any classroom yet.
            </p>
          ) : (
            <ul className="mt-2 space-y-2">
              {state.myClassrooms.map((room) => (
                <li
                  key={`${room.classroom_id}-${room.role}`}
                  className="rounded-md border border-slate-700 bg-slate-900/60 p-2 text-sm"
                >
                  <span className="font-semibold">{room.classroom_name}</span> ·{" "}
                  {room.role} · code: {room.invite_code}
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      {isTeacherLike ? (
        <div className="grid gap-4 lg:grid-cols-2">
          <form className="card space-y-3" onSubmit={handleCreateClassroom}>
            <h2 className="panel-title">Teacher mode: create classroom</h2>
            <input
              className="input"
              placeholder="Classroom name"
              value={createName}
              onChange={(e) => setCreateName(e.target.value)}
              maxLength={120}
            />
            <select
              className="input"
              value={createGradeBand}
              onChange={(e) => setCreateGradeBand(e.target.value)}
            >
              <option value="grades_4_6">Grades 4–6</option>
              <option value="grades_7_8">Grades 7–8</option>
              <option value="mixed">Mixed</option>
            </select>
            <button className="btn-primary" type="submit" disabled={busy}>
              {busy ? "Creating..." : "Create Classroom"}
            </button>
          </form>

          <div className="card">
            <h2 className="panel-title">Teacher classrooms</h2>
            {state.teacherClassrooms.length === 0 ? (
              <p className="mt-2 text-sm text-slate-400">
                No teacher classrooms yet.
              </p>
            ) : (
              <ul className="mt-2 space-y-2">
                {state.teacherClassrooms.map((room) => (
                  <li
                    key={room.id}
                    className={classNames(
                      "rounded-md border p-2 text-sm",
                      state.selectedClassroomId === room.id
                        ? "border-sky-500/50 bg-sky-950/20"
                        : "border-slate-700 bg-slate-900/60",
                    )}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <div>
                        <p className="font-semibold">{room.name}</p>
                        <p className="text-slate-300">
                          Invite:{" "}
                          <span className="font-mono">{room.invite_code}</span>{" "}
                          · Students: {room.student_count}
                        </p>
                      </div>
                      <div className="flex gap-2">
                        <button
                          className="btn-secondary text-xs"
                          onClick={() => void handleLoadStudents(room.id)}
                        >
                          View students
                        </button>
                        <button
                          className="btn-secondary text-xs"
                          onClick={() => void handleRotateInvite(room.id)}
                          disabled={busy}
                        >
                          Rotate code
                        </button>
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      ) : null}

      {state.selectedClassroomStudents ? (
        <div className="card">
          <h2 className="panel-title">
            Student list · {state.selectedClassroomStudents.classroom.name}
          </h2>
          {state.selectedClassroomStudents.students.length === 0 ? (
            <p className="mt-2 text-sm text-slate-400">No students yet.</p>
          ) : (
            <div className="mt-3 overflow-auto">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="text-left text-slate-400">
                    <th className="px-2 py-1">Student</th>
                    <th className="px-2 py-1">XP</th>
                    <th className="px-2 py-1">Completed lessons</th>
                    <th className="px-2 py-1">Streak</th>
                  </tr>
                </thead>
                <tbody>
                  {state.selectedClassroomStudents.students.map((student) => (
                    <tr
                      key={student.user_id}
                      className="border-t border-slate-800"
                    >
                      <td className="px-2 py-1">
                        {student.username || student.email || "Guest learner"}
                      </td>
                      <td className="px-2 py-1">{student.total_xp}</td>
                      <td className="px-2 py-1">{student.completed_lessons}</td>
                      <td className="px-2 py-1">🔥 {student.streak_days}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      ) : null}

      {state.error ? (
        <div className="card border-rose-700/50 text-rose-300">
          {state.error}
        </div>
      ) : null}
      {infoMessage ? <div className="card text-sm">{infoMessage}</div> : null}
    </section>
  );
}

function PlaygroundPage({ auth }: { auth: AuthState }) {
  const [code, setCode] = useState(DEFAULT_PLAYGROUND_CODE);
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState<RunResponse | null>(null);

  async function runPlayground() {
    if (!code.trim()) return;
    setRunning(true);
    try {
      const token = authStore.getAccessToken() ?? undefined;
      const response = await api.runCode({ code }, token);
      setResult(response);
    } catch (err) {
      setResult({
        ok: false,
        stdout: "",
        stderr: getErrorMessage(err),
        compile_error: null,
        duration_ms: 0,
        message: "Playground run failed.",
        exit_code: null,
      });
    } finally {
      setRunning(false);
    }
  }

  return (
    <section className="container-page">
      <div className="mb-4 flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Rust Playground</h1>
          <p className="mt-1 text-sm text-slate-400">
            Experiment freely. Small experiments are a powerful way to learn.
          </p>
        </div>
        <button
          className="btn-primary"
          onClick={runPlayground}
          disabled={running}
        >
          {running ? "Running..." : "Run"}
        </button>
      </div>

      {!auth.user ? (
        <div className="card mb-4 border-amber-600/50 bg-amber-950/20 text-amber-100">
          Tip: sign in as guest/account if you want consistent limits and saved
          learning data.
        </div>
      ) : null}

      <div className="card p-0">
        <Editor
          height="420px"
          defaultLanguage="rust"
          theme="vs-dark"
          value={code}
          onChange={(v) => setCode(v ?? "")}
          options={{
            minimap: { enabled: false },
            automaticLayout: true,
            fontSize: 15,
          }}
        />
      </div>

      {result ? (
        <div className="mt-4 grid gap-4 md:grid-cols-2">
          <OutputPanel title="stdout" value={result.stdout} />
          <OutputPanel
            title="stderr / compile errors"
            value={result.compile_error || result.stderr}
            tone={result.ok ? "neutral" : "danger"}
          />
        </div>
      ) : null}
    </section>
  );
}

function AppRoutes({
  auth,
  progress,
  refreshServerProgress,
  authMode,
  setAuthMode,
  authError,
  onGuestAuth,
  onLoginAuth,
  onSignupAuth,
  kidCopy,
}: {
  auth: AuthState;
  progress: AppProgressState;
  refreshServerProgress: () => Promise<void>;
  authMode: AuthCardMode;
  setAuthMode: (mode: AuthCardMode) => void;
  authError: string | null;
  onGuestAuth: (nickname: string) => Promise<void>;
  onLoginAuth: (identifier: string, password: string) => Promise<void>;
  onSignupAuth: (
    email: string,
    username: string,
    password: string,
  ) => Promise<void>;
  kidCopy: KidModeCopyResponse;
}) {
  return (
    <Routes>
      <Route
        path="/"
        element={
          <HomePage
            auth={auth}
            authMode={authMode}
            setAuthMode={setAuthMode}
            authError={authError}
            onGuestAuth={onGuestAuth}
            onLoginAuth={onLoginAuth}
            onSignupAuth={onSignupAuth}
            progress={progress}
            kidCopy={kidCopy}
          />
        }
      />
      <Route
        path="/learn"
        element={<LearnPage progress={progress} auth={auth} />}
      />
      <Route
        path="/learn/:trackId/:lessonId"
        element={
          <LessonPage
            auth={auth}
            progress={progress}
            refreshServerProgress={refreshServerProgress}
            kidCopy={kidCopy}
          />
        }
      />
      <Route path="/classroom" element={<ClassroomPage auth={auth} />} />
      <Route path="/playground" element={<PlaygroundPage auth={auth} />} />
      <Route path="/home" element={<Navigate to="/" replace />} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  const [auth, setAuth] = useState<AuthState>({
    bootstrapping: true,
    loading: false,
    user: null,
    error: null,
  });

  const [progress, setProgress] = useState<AppProgressState>({
    totalXp: 0,
    completedLessonIds: [],
    lessonAttemptMap: {},
    streak: null,
    badges: [],
  });

  const [kidCopy, setKidCopy] = useState<KidModeCopyResponse>(DEFAULT_KID_COPY);

  const [authMode, setAuthMode] = useState<AuthCardMode>("guest");
  const [authUiError, setAuthUiError] = useState<string | null>(null);

  async function refreshServerProgress(): Promise<void> {
    const token = authStore.getAccessToken();
    if (!token) {
      setProgress({
        totalXp: 0,
        completedLessonIds: [],
        lessonAttemptMap: {},
        streak: null,
        badges: [],
      });
      return;
    }

    const payload = await api.meProgress(token);
    setProgress(progressFromApi(payload));
  }

  async function bootstrapSession(): Promise<void> {
    setAuth((prev) => ({ ...prev, bootstrapping: true, error: null }));
    setAuthUiError(null);

    try {
      const copy = await api.kidModeCopy();
      setKidCopy(copy);
    } catch {
      setKidCopy(DEFAULT_KID_COPY);
    }

    const existingToken = authStore.getAccessToken();
    if (!existingToken) {
      setAuth({
        bootstrapping: false,
        loading: false,
        user: null,
        error: null,
      });
      setProgress({
        totalXp: 0,
        completedLessonIds: [],
        lessonAttemptMap: {},
        streak: null,
        badges: [],
      });
      return;
    }

    try {
      const me = await api.me(existingToken);
      setAuth({ bootstrapping: false, loading: false, user: me, error: null });
      await refreshServerProgress();
    } catch {
      try {
        const refreshRes = await api.authRefresh();
        setAuth({
          bootstrapping: false,
          loading: false,
          user: refreshRes.user,
          error: null,
        });
        await refreshServerProgress();
      } catch {
        api.logout();
        setAuth({
          bootstrapping: false,
          loading: false,
          user: null,
          error: "Session expired. Please sign in again.",
        });
        setProgress({
          totalXp: 0,
          completedLessonIds: [],
          lessonAttemptMap: {},
          streak: null,
          badges: [],
        });
      }
    }
  }

  useEffect(() => {
    void bootstrapSession();
  }, []);

  async function handleGuestAuth(nickname: string): Promise<void> {
    setAuthUiError(null);
    setAuth((prev) => ({ ...prev, loading: true }));
    try {
      const res = await api.authGuest({ nickname: nickname || null });
      setAuth({
        bootstrapping: false,
        loading: false,
        user: res.user,
        error: null,
      });
      await refreshServerProgress();
    } catch (err) {
      setAuth((prev) => ({ ...prev, loading: false }));
      setAuthUiError(getErrorMessage(err));
    }
  }

  async function handleLoginAuth(
    identifier: string,
    password: string,
  ): Promise<void> {
    setAuthUiError(null);
    setAuth((prev) => ({ ...prev, loading: true }));
    try {
      const res = await api.authLogin({ identifier, password });
      setAuth({
        bootstrapping: false,
        loading: false,
        user: res.user,
        error: null,
      });
      await refreshServerProgress();
    } catch (err) {
      setAuth((prev) => ({ ...prev, loading: false }));
      setAuthUiError(getErrorMessage(err));
    }
  }

  async function handleSignupAuth(
    email: string,
    username: string,
    password: string,
  ): Promise<void> {
    setAuthUiError(null);
    setAuth((prev) => ({ ...prev, loading: true }));
    try {
      const res = await api.authSignup({
        email,
        username: username || null,
        password,
      });
      setAuth({
        bootstrapping: false,
        loading: false,
        user: res.user,
        error: null,
      });
      await refreshServerProgress();
    } catch (err) {
      setAuth((prev) => ({ ...prev, loading: false }));
      setAuthUiError(getErrorMessage(err));
    }
  }

  function handleLogout(): void {
    api.logout();
    setAuth({ bootstrapping: false, loading: false, user: null, error: null });
    setProgress({
      totalXp: 0,
      completedLessonIds: [],
      lessonAttemptMap: {},
      streak: null,
      badges: [],
    });
    setAuthMode("guest");
    setAuthUiError(null);
  }

  if (auth.bootstrapping) {
    return (
      <div className="min-h-screen bg-slate-950 text-slate-100">
        <section className="container-page">
          <div className="card">Loading your learning space...</div>
        </section>
      </div>
    );
  }

  return (
    <BrowserRouter>
      <AppLayout auth={auth} progress={progress} onLogout={handleLogout}>
        <AppRoutes
          auth={auth}
          progress={progress}
          refreshServerProgress={refreshServerProgress}
          authMode={authMode}
          setAuthMode={setAuthMode}
          authError={authUiError || auth.error}
          onGuestAuth={handleGuestAuth}
          onLoginAuth={handleLoginAuth}
          onSignupAuth={handleSignupAuth}
          kidCopy={kidCopy}
        />
      </AppLayout>
    </BrowserRouter>
  );
}
